import React, { useMemo } from "react";
import {
  PositionData,
  ClosedPositionData,
} from "@crypto-trading/common/browser";
import {
  useTokenLivePrice,
  LivePriceExchangeCode,
} from "@/hooks/useTokenLivePrice";
import { useInView } from "@/hooks/useInView";

interface PositionTileProps {
  position: PositionData | ClosedPositionData;
  onClick?: () => void;
}

const isClosedPosition = (
  pos: PositionData | ClosedPositionData
): pos is ClosedPositionData => {
  return "closedAt" in pos && pos.closedAt !== null;
};

/**
 * Map exchange code/name to live price exchange code
 */
const mapExchangeCode = (
  exchangeCode?: string,
  exchangeName?: string
): LivePriceExchangeCode => {
  const code = (exchangeCode || exchangeName || "").toLowerCase();
  if (code.includes("binance")) return "binance";
  if (code.includes("bybit")) return "bybit";
  if (code.includes("gate")) return "gate";
  if (code.includes("kucoin")) return "kucoin";
  if (code.includes("aster")) return "asterdex";
  return "binance"; // Default
};

/**
 * Calculate unrealized PnL based on position data
 */
const calculateUnrealizedPnL = (
  entryPrice: number,
  markPrice: number,
  usdAmount: number,
  leverage: number,
  positionType: string
): { pnl: number; pnlPercentage: number } => {
  if (!entryPrice || !markPrice || entryPrice === 0) {
    return { pnl: 0, pnlPercentage: 0 };
  }

  const isLong = positionType.toUpperCase() === "LONG";
  const priceChange = isLong
    ? (markPrice - entryPrice) / entryPrice
    : (entryPrice - markPrice) / entryPrice;

  const pnl = usdAmount * leverage * priceChange;
  const pnlPercentage = priceChange * leverage * 100;

  return { pnl, pnlPercentage };
};

export const PositionTile: React.FC<PositionTileProps> = ({
  position,
  onClick,
}) => {
  const closed = isClosedPosition(position);
  const isAutotraded = position.tradeOrigin === "AUTO_TRADE";
  const isCopyTraded = position.tradeOrigin === "COPY_TRADE";

  // Check if position exists on exchange (only relevant for open positions)
  const existsOnExchange = closed
    ? true
    : ((position as PositionData).existsOnExchange ?? true);
  const isClosedExternally = !closed && !existsOnExchange;

  // Viewport detection - only subscribe to live prices when visible
  const [tileRef, isInView] = useInView<HTMLButtonElement>({
    rootMargin: "50px",
    threshold: 0.1,
  });

  // Get exchange code for live price subscription
  const exchangeCode = useMemo(
    () => mapExchangeCode(position.exchangeCode, position.exchangeName),
    [position.exchangeCode, position.exchangeName]
  );

  // Subscribe to live price updates when tile is visible (only for open positions that exist on exchange)
  const { price: liveMarkPrice, isSubscribed } = useTokenLivePrice(
    position.symbol,
    exchangeCode,
    isInView && !closed && existsOnExchange // Only subscribe for open positions that exist on exchange and are visible
  );

  // Calculate display values
  const entryPrice =
    typeof position.entryPrice === "string"
      ? parseFloat(position.entryPrice)
      : position.entryPrice;
  // markPrice only exists on PositionData (open positions)
  const storedMarkPrice =
    !closed && "markPrice" in position
      ? typeof position.markPrice === "string"
        ? parseFloat(position.markPrice)
        : (position.markPrice ?? 0)
      : 0;
  const usdAmount =
    typeof position.usdAmount === "string"
      ? parseFloat(position.usdAmount)
      : position.usdAmount;
  const leverage =
    typeof position.leverage === "string"
      ? parseFloat(position.leverage)
      : position.leverage;

  // Use live mark price if available, otherwise use stored mark price
  const displayMarkPrice = liveMarkPrice ?? storedMarkPrice ?? 0;

  // Calculate live PnL for open positions
  const livePnL = useMemo(() => {
    if (closed || !displayMarkPrice) return null;
    return calculateUnrealizedPnL(
      entryPrice,
      displayMarkPrice,
      usdAmount,
      leverage,
      position.positionType
    );
  }, [
    closed,
    displayMarkPrice,
    entryPrice,
    usdAmount,
    leverage,
    position.positionType,
  ]);

  const formatPrice = (price: number | string | null | undefined) => {
    if (!price) return "N/A";
    const numPrice = typeof price === "string" ? parseFloat(price) : price;
    return `$${numPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })}`;
  };

  const formatAmount = (amount: number | string) => {
    const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;
    return `$${numAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Use live PnL for open positions, realized PnL for closed positions
  const pnl = closed
    ? position.realizedPnl
    : (livePnL?.pnl ?? parseFloat((position as PositionData).pnl || "0"));
  const pnlPercentage = closed
    ? position.pnlPercentage
    : (livePnL?.pnlPercentage ??
      parseFloat((position as PositionData).pnlPercentage || "0"));
  const isProfitable = pnl !== null && pnl !== undefined && pnl >= 0;

  return (
    <button
      ref={tileRef}
      onClick={onClick}
      className={`bg-secondary max-w-[500px] xl:max-w-[700px] rounded-lg p-3 border border-gray-500/30 cursor-pointer hover:border-accent/40 transition-colors w-full text-left ${isClosedExternally ? "opacity-50" : ""}`}
    >
      {/* Closed Externally Warning */}
      {isClosedExternally && (
        <div className="mb-2 px-3 py-2 bg-yellow-500/10 border border-yellow-500/30 rounded-md">
          <p className="text-xs text-yellow-400">
            ⚠️ Position closed externally - we will update information about it
            shortly
          </p>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between border-b border-gray-500/30 pb-2 ">
        <div className="flex flex-col w-full">
          <div className="flex items-center justify-between gap-2 w-full">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-bold text-text-primary">
                {position.symbol}
              </h3>
              <span className={`px-2 py-0.5 rounded-full text-xs  bg-black/50`}>
                {position.positionType}
              </span>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2 text-right">
              {isAutotraded && (
                <span className="px-2 py-0.5 rounded-full text-xs bg-accent/10 whitespace-nowrap">
                  Autotraded 🤖
                </span>
              )}
              {isCopyTraded && (
                <span className="px-2 py-0.5 rounded-full text-xs bg-accent/10 whitespace-nowrap">
                  Copy traded 📋
                </span>
              )}
              <span className="text-xs text-accent rounded-full px-2 py-0.5 bg-accent/10 capitalize whitespace-nowrap">
                {position.exchangeName || "N/A"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Stats Grid */}
      <div className="grid grid-cols-3 gap-4 pt-2 xl:hidden">
        {/* Amount */}
        <div className="border-r border-gray-500/30">
          <p className="text-xs text-text-secondary mb-1">Amount</p>
          <p className="text-sm text-text-primary">
            {formatAmount(position.usdAmount)}
          </p>
          <p className="text-xs text-text-secondary mt-0.5">
            Leverage: {position.leverage}x
          </p>
        </div>

        {/* Entry/Exit */}
        <div className="border-r border-gray-500/30">
          <p className="text-xs text-text-secondary mb-1">Entry</p>
          <p className="text-sm text-text-primary">
            {formatPrice(position.entryPrice)}
          </p>
          {closed && position.exitPrice && (
            <p className="text-xs text-text-secondary mt-0.5">
              Exit: {formatPrice(position.exitPrice)}
            </p>
          )}
          {!closed && (
            <div className="flex items-center gap-1 mt-0.5">
              <p
                className={`text-xs text-text-secondary ${isSubscribed ? "transition-all duration-150" : ""}`}
              >
                Mark: {formatPrice(displayMarkPrice)}
              </p>
              {isSubscribed && (
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-500"></span>
                </span>
              )}
            </div>
          )}
        </div>

        {/* PNL */}
        <div>
          <p className="text-xs text-text-secondary mb-1">PNL</p>
          <div className="flex items-center gap-1">
            {(pnl || 0) >= 0 ? (
              <img
                src="/icons/profit.png"
                alt="Trending Up"
                className={`w-6 h-4 object-contain`}
              />
            ) : (
              <img
                src="/icons/loss.png"
                alt="Trending Down"
                className={`w-6 h-4 object-contain`}
              />
            )}
            <div>
              <p
                className={`text-sm ${isProfitable ? "text-green-400" : "text-red-400"}`}
              >
                {pnl !== null && pnl !== undefined
                  ? `$${pnl.toFixed(2)}`
                  : "N/A"}
              </p>
              {pnlPercentage !== null && pnlPercentage !== undefined && (
                <p
                  className={`text-xs ${isProfitable ? "text-green-400" : "text-red-400"}`}
                >
                  ({pnlPercentage.toFixed(2)}%)
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Horizontal Layout */}
      <div className="hidden xl:flex items-start justify-start gap-4 pt-2">
        {/* Amount */}
        <div className="flex flex-col items-start flex-1">
          <p className="text-xs text-text-secondary mb-1">Amount</p>
          <p className="text-sm text-text-primary font-medium">
            {formatAmount(position.usdAmount)}
          </p>
        </div>

        {/* Divider */}
        <div className="h-10 w-px bg-gray-500/30 flex-shrink-0" />

        {/* Leverage */}
        <div className="flex flex-col items-start flex-1">
          <p className="text-xs text-text-secondary mb-1">Leverage</p>
          <p className="text-sm text-text-primary font-medium">
            {position.leverage}x
          </p>
        </div>

        {/* Divider */}
        <div className="h-10 w-px bg-gray-500/30 flex-shrink-0" />

        {/* Entry Price */}
        <div className="flex flex-col items-start flex-1">
          <p className="text-xs text-text-secondary mb-1">Entry Price</p>
          <p className="text-sm text-text-primary font-medium">
            {formatPrice(position.entryPrice)}
          </p>
        </div>

        {/* Divider */}
        <div className="h-10 w-px bg-gray-500/30 flex-shrink-0" />

        {/* Exit/Mark Price */}
        <div className="flex flex-col items-start flex-1">
          {closed ? (
            <>
              <p className="text-xs text-text-secondary mb-1">Exit Price</p>
              <p className="text-sm text-text-primary font-medium">
                {formatPrice(position.exitPrice)}
              </p>
            </>
          ) : (
            <>
              <div className="flex items-center gap-1 mb-1">
                <p className="text-xs text-text-secondary">Mark Price</p>
                {isSubscribed && (
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-500"></span>
                  </span>
                )}
              </div>
              <p
                className={`text-sm text-text-primary font-medium ${isSubscribed ? "transition-all duration-150" : ""}`}
              >
                {isClosedExternally ? "---" : formatPrice(displayMarkPrice)}
              </p>
            </>
          )}
        </div>

        {/* Divider */}
        <div className="h-10 w-px bg-gray-500/30 flex-shrink-0" />

        {/* PNL */}
        <div className="flex flex-col items-start flex-[1.5]">
          <p className="text-xs text-text-secondary mb-1">PNL</p>
          <div className="flex items-center gap-2">
            {(pnl || 0) >= 0 ? (
              <img
                src="/icons/profit.png"
                alt="Trending Up"
                className="w-6 h-4 object-contain"
              />
            ) : (
              <img
                src="/icons/loss.png"
                alt="Trending Down"
                className="w-6 h-4 object-contain"
              />
            )}
            <div className="flex items-baseline gap-1">
              <p
                className={`text-sm font-medium ${isProfitable ? "text-green-400" : "text-red-400"}`}
              >
                {pnl !== null && pnl !== undefined && !isClosedExternally
                  ? `$${pnl.toFixed(2)}`
                  : "---"}
              </p>
              {pnlPercentage !== null && pnlPercentage !== undefined && (
                <p
                  className={`text-xs ${isProfitable ? "text-green-400" : "text-red-400"}`}
                >
                  ({isClosedExternally ? "---" : pnlPercentage.toFixed(2)}%)
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </button>
  );
};
